import { z } from 'zod';
import { ConfidenceBand } from './vocabularies';
import { IsoDate, Timestamp, Uuid } from './envelope';
import { MetricValue } from './metric';

/**
 * Outcome traceability read-models — "did the work we shipped move the outcome
 * we said it would?" (UC-09-066).
 *
 * One projection, `OutcomeTraceabilityView`, over a chain that every delivery
 * organisation draws informally and almost nobody draws honestly:
 *
 *     work item  →  release  →  product outcome  →  measured metric movement
 *
 * The chain is easy to render and easy to falsify, so the types here are shaped
 * around the four ways it is normally faked.
 *
 * **A hop is not a fact.** Every edge is a `TraceLink` carrying how it came to
 * exist — a team declared it, a rule inferred it from a ticket reference, a rule
 * inferred it from nothing but the dates lining up, or it does not exist at all.
 * The last two are not the first, and `NotLinked` is a first-class value rather
 * than an absent row, because an absent row disappears from a count.
 *
 * **A claim is not a measurement.** `ClaimedContribution` and `OutcomeMovement`
 * are separate types on purpose and can never be merged into one number: the
 * first is what a team asserted in a business case, the second is what the
 * instrument recorded. A screen that adds them has invented evidence.
 *
 * **A metric that changed definition mid-period has no delta.** `OutcomeMovement`
 * carries both definition versions and a `measurable` flag that is allowed to be
 * false. When it is false there is no number to show and `unmeasurableReason` is
 * what gets rendered in its place — an attribution computed across a definition
 * change is worse than no attribution, because it looks like one.
 *
 * **Work that maps to nothing is the finding.** `UnlinkedWorkGroup` and
 * `OutcomeWithoutDelivery` are not error states. They are the two most useful
 * populations on the screen, and the coverage figures reconcile against the
 * whole period's throughput so that neither can be quietly dropped.
 */

// ── Shared refs ──────────────────────────────────────────────────────────────

/**
 * The slice of a product these views need. Deliberately thin: an outcome card
 * that depended on the Product 360 row would break every time that row moved.
 */
export const OutcomeProductRef = z.object({
  id: Uuid,
  businessCode: z.string(),
  name: z.string().min(1),
  route: z.string().nullable(),
});
export type OutcomeProductRef = z.infer<typeof OutcomeProductRef>;

export const WorkItemKind = z.enum([
  'Feature',
  'Enhancement',
  'Defect',
  'TechnicalDebt',
  'Compliance',
  'Operational',
]);
export type WorkItemKind = z.infer<typeof WorkItemKind>;

export const OutcomeWorkItemRef = z.object({
  id: Uuid,
  /** The tracker key a reader can paste into search: 'AUR-14822'. */
  reference: z.string().min(1),
  title: z.string().min(1),
  kind: WorkItemKind,
  teamName: z.string(),
  /** Null while the item is still open; these views only carry shipped work. */
  closedAt: IsoDate.nullable(),
  /**
   * Story points, or null. Never rendered as 0: a team that does not estimate
   * has unknown effort, not zero effort, so the reason is required alongside.
   */
  effortPoints: z.number().nullable(),
  effortUnavailableReason: z.string().nullable(),
  /**
   * Which tool this item came from, and where to open it.
   *
   * The chain names a work item and the reader's next question is always the
   * same: show me the ticket. Two systems can legitimately hold an item with
   * the same key, so the tool has to be named alongside the link rather than
   * assumed — `AUR-181` in Jira and `AUR-181` in a service desk are different
   * pieces of work, and a chain that said only "AUR-181" would be inviting the
   * reader to check the wrong one.
   *
   * Both null for an item that arrived in a spreadsheet: a workbook row has no
   * page to open, and offering a link that went nowhere would be worse than
   * offering none.
   */
  sourceSystem: z.string().nullable(),
  sourceUrl: z.string().url().nullable(),
});
export type OutcomeWorkItemRef = z.infer<typeof OutcomeWorkItemRef>;

export const OutcomeReleaseRef = z.object({
  id: Uuid,
  /** 'R26.04-12' — the code that appears in the release train. */
  code: z.string().min(1),
  name: z.string().min(1),
  product: OutcomeProductRef,
  status: z.enum(['Planned', 'InFlight', 'Released', 'RolledBack']),
  plannedDate: IsoDate,
  /** Null until it actually shipped. A planned date is not a shipping date. */
  actualDate: IsoDate.nullable(),
  /** How many shipped work items the release carried, in total. */
  workItemCount: z.number().int().nonnegative(),
});
export type OutcomeReleaseRef = z.infer<typeof OutcomeReleaseRef>;

export const OutcomeHorizon = z.enum(['Quarter', 'Year', 'MultiYear']);
export type OutcomeHorizon = z.infer<typeof OutcomeHorizon>;

export const ProductOutcomeRef = z.object({
  id: Uuid,
  /** 'OUT-CDR-01'. Stable across restatements of the wording. */
  code: z.string().min(1),
  /** Short handle for a picker: 'Recover a disrupted roster within the hour'. */
  name: z.string().min(1),
  /** The full sentence the business committed to, verbatim. */
  statement: z.string().min(1),
  product: OutcomeProductRef,
  ownerName: z.string(),
  horizon: OutcomeHorizon,
  /** The governed metric that is supposed to prove it. */
  metricId: z.string().min(1),
  metricLabel: z.string().min(1),
});
export type ProductOutcomeRef = z.infer<typeof ProductOutcomeRef>;

// ── The link — the whole point of the screen ─────────────────────────────────

/**
 * How an edge in the chain came to exist.
 *
 * Ordered by how much weight it deserves, and never collapsed into a boolean:
 *
 *  - `DeclaredByTeam` — a human asserted the link and is named for it.
 *  - `InferredFromReference` — a rule read a ticket key out of a commit, a
 *    release note or an OKR field. Mechanical, checkable, occasionally wrong.
 *  - `InferredFromTiming` — nothing connects the two except that they happened
 *    in the same window. This is the one that fakes an entire screen if it is
 *    allowed to look like the other two, so the UI is required to mark it.
 *  - `NotLinked` — no link exists. Carried explicitly so it can be counted.
 */
export const OutcomeLinkType = z.enum([
  'DeclaredByTeam',
  'InferredFromReference',
  'InferredFromTiming',
  'NotLinked',
]);
export type OutcomeLinkType = z.infer<typeof OutcomeLinkType>;

export const TraceEvidence = z.object({
  /** One line in the reader's language: "Release note names OUT-CDR-01". */
  label: z.string().min(1),
  /** The checkable particular behind it: the commit sha, the field, the row. */
  detail: z.string().nullable(),
  sourceSystem: z.string().nullable(),
  observedAt: Timestamp.nullable(),
});
export type TraceEvidence = z.infer<typeof TraceEvidence>;

/**
 * One edge of the chain.
 *
 * `evidence` may be empty only when `type` is `NotLinked` or
 * `InferredFromTiming` — in the first case there is nothing to evidence, and in
 * the second the absence of evidence *is* the finding. For the other two kinds a
 * consumer should treat an empty array as a data defect: a declared link with no
 * declarer is an anonymous assertion.
 */
export const TraceLink = z.object({
  type: OutcomeLinkType,
  confidence: ConfidenceBand,
  /** 0–1, or null where no rule produced a score (a human declaration). */
  confidenceScore: z.number().min(0).max(1).nullable(),
  evidence: z.array(TraceEvidence),
  /** Who or what created the link: a person's name, or a rule id. */
  establishedBy: z.string().nullable(),
  establishedAt: Timestamp.nullable(),
  /** The sentence that says what this link does *not* prove. */
  caveat: z.string().nullable(),
});
export type TraceLink = z.infer<typeof TraceLink>;

export const TracedWorkItem = z.object({
  item: OutcomeWorkItemRef,
  /** Work item → release. */
  link: TraceLink,
});
export type TracedWorkItem = z.infer<typeof TracedWorkItem>;

// ── Measurement ──────────────────────────────────────────────────────────────

export const OutcomeSeriesPoint = z.object({
  /** 'YYYY-MM'. Monthly is the finest grain any of these metrics is governed at. */
  period: z.string().regex(/^\d{4}-\d{2}$/),
  /** Null where the metric was not computed that month. Never substituted with 0. */
  value: z.number().nullable(),
  unavailableReason: z.string().nullable(),
  /** Carried per point so a definition change is visible in the series itself. */
  definitionVersion: z.string(),
});
export type OutcomeSeriesPoint = z.infer<typeof OutcomeSeriesPoint>;

export const MovementDirection = z.enum(['Improved', 'Worsened', 'Flat', 'NotMeasurable']);
export type MovementDirection = z.infer<typeof MovementDirection>;

/**
 * What the instrument recorded — and nothing else.
 *
 * No field here refers to a release, a team or a claim. That separation is
 * structural rather than stylistic: it makes it impossible to write a renderer
 * that reads a delivery fact out of a measurement, which is how "we shipped it
 * and the number moved" becomes "we moved the number".
 */
export const OutcomeMovement = z.object({
  /** The governed current value, with its own provenance and status. */
  metric: MetricValue,
  baselineLabel: z.string(),
  baselineValue: z.number().nullable(),
  baselineUnavailableReason: z.string().nullable(),
  currentValue: z.number().nullable(),
  deltaAbsolute: z.number().nullable(),
  deltaPercent: z.number().nullable(),
  direction: MovementDirection,
  /** The committed target, where the outcome named one. */
  target: z.number().nullable(),
  targetUnavailableReason: z.string().nullable(),
  /**
   * False when a delta across this window would be meaningless — most often
   * because the metric's definition changed inside it. When false, every
   * numeric field above is null and `unmeasurableReason` is the only thing a
   * consumer may render in their place.
   */
  measurable: z.boolean(),
  unmeasurableReason: z.string().nullable(),
  definitionVersionAtBaseline: z.string(),
  definitionVersionNow: z.string(),
  /** Null when the definition held for the whole window. */
  definitionChangedAt: IsoDate.nullable(),
  definitionChangeNote: z.string().nullable(),
  series: z.array(OutcomeSeriesPoint),
  /**
   * Everything else that plausibly moved this metric in the same window.
   *
   * Required — as an explicitly empty array if the analyst genuinely found
   * none — because a movement panel with no confounders listed reads as a
   * controlled experiment, and none of these ever are.
   */
  confounders: z.array(
    z.object({
      label: z.string().min(1),
      detail: z.string().nullable(),
      /** When it happened, so the reader can see it overlapping the release. */
      occurredAt: IsoDate.nullable(),
    }),
  ),
});
export type OutcomeMovement = z.infer<typeof OutcomeMovement>;

// ── Claim ────────────────────────────────────────────────────────────────────

export const ClaimBasis = z.enum([
  'TeamAssertion',
  'BusinessCaseForecast',
  'VendorEstimate',
  'ControlledExperiment',
  'None',
]);
export type ClaimBasis = z.infer<typeof ClaimBasis>;

/**
 * What somebody said the work would do. Never a measurement, whatever its
 * precision — a business case quoting 4.2 percentage points to one decimal is a
 * forecast with a decimal point, and the type keeps it that way.
 */
export const ClaimedContribution = z.object({
  /** 0–1 of the outcome's movement claimed for this delivery, where one was stated. */
  claimedSharePercent: z.number().min(0).max(1).nullable(),
  shareUnavailableReason: z.string().nullable(),
  statement: z.string().min(1),
  claimedBy: z.string(),
  claimedAt: Timestamp,
  basis: ClaimBasis,
  /** Did anyone independent check it? Usually no, and the screen should say so. */
  corroborated: z.boolean(),
  corroborationNote: z.string().nullable(),
});
export type ClaimedContribution = z.infer<typeof ClaimedContribution>;

// ── Verdict ──────────────────────────────────────────────────────────────────

/**
 * What the chain, taken as a whole, is entitled to say.
 *
 *  - `Measured`   — a real movement, a strong link, and the confounders were
 *                   assessed. This is rare and should look rare.
 *  - `Consistent` — the movement happened and the link holds, but nothing rules
 *                   out the confounders. Consistent with the claim, not proof.
 *  - `ClaimOnly`  — somebody asserted a contribution; nothing measured it.
 *  - `NoMovement` — the work shipped and the metric did not move. Reported as
 *                   plainly as a success would be.
 *  - `Refused`    — an attribution cannot be computed at all, and why.
 */
export const AttributionState = z.enum([
  'Measured',
  'Consistent',
  'ClaimOnly',
  'NoMovement',
  'Refused',
]);
export type AttributionState = z.infer<typeof AttributionState>;

export const AttributionVerdict = z.object({
  state: AttributionState,
  /** Six words at most; it sits in a pill. */
  headline: z.string().min(1),
  /** The reasoning, in full sentences. Rendered before any figure. */
  statement: z.string().min(1),
  /** Populated only when `state` is `Refused`. */
  refusalReason: z.string().nullable(),
  confidence: ConfidenceBand,
});
export type AttributionVerdict = z.infer<typeof AttributionVerdict>;

// ── The chain ────────────────────────────────────────────────────────────────

export const OutcomeTraceChain = z.object({
  id: Uuid,
  outcome: ProductOutcomeRef,
  release: OutcomeReleaseRef,
  /** Release → outcome. The hop that carries almost all of the doubt. */
  releaseLink: TraceLink,
  workItems: z.array(TracedWorkItem),
  /**
   * Work items in this release that reached no outcome. Counted here as well as
   * in `UnlinkedWorkGroup` so a reader looking at one release can see what it
   * carried that nobody has connected to anything.
   */
  unlinkedWorkItemCount: z.number().int().nonnegative(),
  claim: ClaimedContribution.nullable(),
  claimAbsentReason: z.string().nullable(),
  verdict: AttributionVerdict,
});
export type OutcomeTraceChain = z.infer<typeof OutcomeTraceChain>;

// ── The populations that are the point ───────────────────────────────────────

export const UnlinkedWorkGroup = z.object({
  /** Why these items reach no outcome. The group key. */
  reason: z.string().min(1),
  count: z.number().int().nonnegative(),
  /** 0–1 of all shipped work in the period. */
  sharePercent: z.number().min(0).max(1),
  /** A handful, so the reader can judge whether the reason is fair. */
  examples: z.array(OutcomeWorkItemRef),
  /** What would fix it — a step somebody can take, not an exhortation. */
  remedy: z.string().min(1),
  /**
   * Whether this population is a governance problem at all. Regulatory work and
   * keeping the lights on legitimately serve no product outcome, and counting
   * them as a failure would push teams to invent links.
   */
  expected: z.boolean(),
});
export type UnlinkedWorkGroup = z.infer<typeof UnlinkedWorkGroup>;

export const OutcomeWithoutDelivery = z.object({
  outcome: ProductOutcomeRef,
  /** Why nothing is behind it: no release names it, or nothing shipped at all. */
  reason: z.string().min(1),
  /** Null when nothing has ever been linked to it. */
  lastLinkedDeliveryAt: IsoDate.nullable(),
  /** What the metric did anyway, in words. It often moves without us. */
  movementNote: z.string(),
});
export type OutcomeWithoutDelivery = z.infer<typeof OutcomeWithoutDelivery>;

export const LinkTypeCount = z.object({
  type: OutcomeLinkType,
  count: z.number().int().nonnegative(),
});
export type LinkTypeCount = z.infer<typeof LinkTypeCount>;

/**
 * The reconciliation frame. `linkTypeCounts` must sum to `shippedWorkItemCount`
 * — every shipped item is exactly one of declared, reference-inferred,
 * timing-inferred or unlinked. The view ships both numbers rather than a
 * pre-computed percentage so the consumer can check the sum and say so when it
 * fails, instead of rendering a coverage figure over an incomplete population.
 */
export const DeliveryCoverage = z.object({
  periodLabel: z.string().min(1),
  shippedWorkItemCount: z.number().int().nonnegative(),
  shippedReleaseCount: z.number().int().nonnegative(),
  outcomeCount: z.number().int().nonnegative(),
  linkTypeCounts: z.array(LinkTypeCount),
});
export type DeliveryCoverage = z.infer<typeof DeliveryCoverage>;

// ── The view ─────────────────────────────────────────────────────────────────

export const OutcomeTraceabilityView = z.object({
  periodLabel: z.string().min(1),
  kpis: z.array(MetricValue),
  /** Every outcome in scope, for the picker. Includes ones with no delivery. */
  outcomes: z.array(ProductOutcomeRef),
  selectedOutcomeId: Uuid,
  /** The measurement for the selected outcome — one per outcome, not per chain. */
  movement: OutcomeMovement,
  /** Every delivery chain that claims to reach the selected outcome. */
  chains: z.array(OutcomeTraceChain),
  /** Portfolio-wide, for the whole period — not narrowed by the selection. */
  coverage: DeliveryCoverage,
  unlinkedWork: z.array(UnlinkedWorkGroup),
  outcomesWithoutDelivery: z.array(OutcomeWithoutDelivery),
});
export type OutcomeTraceabilityView = z.infer<typeof OutcomeTraceabilityView>;
